// src/components/MusicGallery.jsx

import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

// Hooks
import { useAudioPlayerContext } from './music/AudioPlayerProvider';
import { useMusicFilters } from './music/hooks/useMusicFilters';
import { usePurchaseModal } from './music/hooks/usePurchaseModal';
import { usePlaylist } from './music/hooks/usePlaylist';
import { usePurchases } from './music/hooks/usePurchases';
import { useIsMobile } from './music/hooks/useIsMobile';
// Add 'Music' to your existing imports
import { Music } from 'lucide-react';

// Constants
import { CREATE_PAYMENT_URL } from './music/constants/paymentConfig';

// Utils

// Views
import { SkeletonLoader } from './music/views/SkeletonLoader';
import { CardView } from './music/views/CardView';
import { TableView } from './music/views/TableView';
import { GroupedView } from './music/views/GroupedView';
import { MobileListView } from './music/views/MobileListView';

// Parts
import { SearchAndFilter } from './music/parts/SearchAndFilter';
import { PurchaseModal } from './music/parts/PurchaseModal';
import { PlaylistManager } from './music/parts/PlaylistManager';
import { PlaylistViewer } from './music/parts/PlaylistViewer';
import { AddToPlaylistModal } from './music/parts/AddToPlaylistModal';
import StarfieldBackground from './music/parts/StarfieldBackground';

export default function MusicGallery({ music, loading, currentUser }) {
  // ========== CUSTOM HOOKS ==========
  const filters = useMusicFilters(music);
  const purchase = usePurchaseModal(CREATE_PAYMENT_URL);
  const player = useAudioPlayerContext();

  const playlist = usePlaylist(currentUser);
  const purchases = usePurchases(currentUser);

  // Fallback for isPurchased if purchases hook is not ready
  const isPurchasedFallback = (musicId) => {
    if (purchases && typeof purchases.isPurchased === 'function') {
      return purchases.isPurchased(musicId);
    }
    return false;
  };

  // ========== LOCAL STATE ==========
  const [groupByAlbum, setGroupByAlbum] = useState(false);
  const [viewMode, setViewMode] = useState('card');
  const [showPlaylistViewer, setShowPlaylistViewer] = useState(false);
  const [showAddToPlaylistModal, setShowAddToPlaylistModal] = useState(false);
  const [trackToAddToPlaylist, setTrackToAddToPlaylist] = useState(null);

  const { currentTrack, isPlaying, playQueue, handlePlayPause: playerHandlePlayPause } = player;

  const isMobile = useIsMobile();

  const useAdSense = () => {
    useEffect(() => {
      if (typeof window.adsbygoogle === 'undefined') return;

      const initAds = () => {
        try {
          if (Array.isArray(window.adsbygoogle)) {
            window.adsbygoogle.push({});
          }
        } catch (e) {
          if (e.message && !e.message.includes('already have ads')) {
            console.log('AdSense error:', e.message);
          }
        }
      };

      const timer = setTimeout(initAds, 300);
      return () => clearTimeout(timer);
    }, []);
  };

  useAdSense();

  // ========== HANDLERS ==========

  const handlePlayPause = useCallback(
    (track) => playerHandlePlayPause(track, filters.filteredMusic),
    [playerHandlePlayPause, filters.filteredMusic]
  );

  const handlePlayAlbum = useCallback(
    (albumName) => {
      const albumTracks = filters.groupedByAlbum[albumName];
      if (albumTracks && albumTracks.length > 0) {
        playerHandlePlayPause(albumTracks[0], albumTracks);
      }
    },
    [filters.groupedByAlbum, playerHandlePlayPause]
  );

  const handlePlayPlaylist = useCallback(
    (track, index) => {
      if (playlist.selectedPlaylist?.tracks) {
        playerHandlePlayPause(track, playlist.selectedPlaylist.tracks);
      }
    },
    [playlist.selectedPlaylist, playerHandlePlayPause]
  );

  const handleAddToPlaylist = useCallback(
    (track) => {
      setTrackToAddToPlaylist(track);
      setShowAddToPlaylistModal(true);
    },
    []
  );

  const handleDownload = async (track) => {
    if (currentUser && purchases.isPurchased && typeof purchases.isPurchased === 'function') {
      if (purchases.isPurchased(track.id)) {
        try {
          const sendEmailUrl = `${process.env.REACT_APP_SEND_MUSIC_EMAIL || 'https://us-central1-asidorsmusic.cloudfunctions.net/sendMusicViaEmail'}`;
          
          const response = await fetch(sendEmailUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: currentUser.uid,
              musicId: track.id,
              userEmail: currentUser.email
            })
          });

          const data = await response.json();

          if (!response.ok || data.error) {
            alert(`Failed to send music: ${data.error || 'Unknown error'}`);
            return;
          }

          alert(`✅ Music file sent to ${currentUser.email}!`);
        } catch (error) {
          console.error('Error sending music email:', error);
          alert('Failed to send music. Please try again.');
        }
      }
    }
  };

  // ========== RENDER ==========

  return (
    <>
      <section
        id="music"
        className="relative overflow-hidden min-h-screen py-10 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-black"
      >
        {/* Animated starfield while music plays */}
        {isPlaying && <StarfieldBackground />}

        <div className="absolute inset-0 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-6 sm:mb-12"
          >
            

            {!currentUser && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm md:text-base text-yellow-400 mt-4"
              >
                ⏱️ Preview mode: Only 15 seconds per seek. Log in to unlock full music!
              </motion.p>
            )}
          </motion.div>

          {/* Content */}
          {loading ? (
            <SkeletonLoader count={6} />
          ) : music.length === 0 ? (
            <div className="fixed inset-0 w-screen h-screen bg-black/100 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center">
            {/* The Spinner Container */}
            <div className="relative w-20 h-20 mb-6">
              {/* Outer Glowing Ring */}
              <div className="absolute inset-0 rounded-full border-4 border-white/5"></div>
              
              {/* Spinning Gradient Ring */}
              <div className="absolute inset-0 rounded-full border-t-4 border-purple-500 animate-spin shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
              
              {/* Inner Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Music size={28} className="text-white/30 animate-pulse" />
              </div>
            </div>

            {/* Status Text */}
            <div className="text-center space-y-2">
              <p className="text-white/80 text-lg font-medium tracking-tight">
                Melody is Loading
              </p>
              <p className="text-white/40 text-xs tracking-[0.2em] uppercase">
                Please wait a moment
              </p>
            </div>
          </div>
          ) : (
            <>
              {/* Search and Filter */}
              <SearchAndFilter
                searchQuery={filters.searchQuery}
                selectedType={filters.selectedType}
                selectedAlbum={filters.selectedAlbum}
                albums={filters.albums}
                groupByAlbum={groupByAlbum}
                viewMode={viewMode}
                filteredMusic={filters.filteredMusic.length}
                totalMusic={music.length}
                onSearchChange={filters.setSearchQuery}
                onTypeChange={filters.setSelectedType}
                onAlbumChange={filters.setSelectedAlbum}
                onGroupChange={setGroupByAlbum}
                onViewModeChange={setViewMode}
              />

              {/* Views */}
              {filters.filteredMusic.length > 0 ? (
                <>
                  {isMobile ? (
                    <MobileListView
                      filteredMusic={filters.filteredMusic}
                      currentTrack={currentTrack}
                      isPlaying={isPlaying}
                      playQueue={playQueue}
                      onPlayPause={handlePlayPause}
                      onPurchase={purchase.handlePurchaseClick}
                      processingPayment={purchase.processingPayment}
                      onAddToPlaylist={handleAddToPlaylist}
                      currentUser={currentUser}
                      isPurchased={isPurchasedFallback}
                      onDownload={handleDownload}
                    />
                  ) : groupByAlbum ? (
                    <GroupedView
                      groupedByAlbum={filters.groupedByAlbum}
                      currentTrack={currentTrack}
                      isPlaying={isPlaying}
                      playQueue={playQueue}
                      onPlayPause={handlePlayPause}
                      onPlayAlbum={handlePlayAlbum}
                      onPurchase={purchase.handlePurchaseClick}
                      processingPayment={purchase.processingPayment}
                      currentUser={currentUser}
                      isPurchased={isPurchasedFallback}
                      onDownload={handleDownload}
                      onAddToPlaylist={handleAddToPlaylist}
                    />
                  ) : viewMode === 'table' ? (
                    <TableView
                      filteredMusic={filters.filteredMusic}
                      currentTrack={currentTrack}
                      isPlaying={isPlaying}
                      playQueue={playQueue}
                      onPlayPause={handlePlayPause}
                      onPurchase={purchase.handlePurchaseClick}
                      processingPayment={purchase.processingPayment}
                      onAddToPlaylist={handleAddToPlaylist}
                      currentUser={currentUser}
                      isPurchased={isPurchasedFallback}
                      onDownload={handleDownload}
                    />
                  ) : (
                    <CardView
                      filteredMusic={filters.filteredMusic}
                      currentTrack={currentTrack}
                      isPlaying={isPlaying}
                      playQueue={playQueue}
                      onPlayPause={handlePlayPause}
                      onPurchase={purchase.handlePurchaseClick}
                      processingPayment={purchase.processingPayment}
                      groupByAlbum={groupByAlbum}
                      onAddToPlaylist={handleAddToPlaylist}
                      currentUser={currentUser}
                      isPurchased={isPurchasedFallback}
                      onDownload={handleDownload}
                    />
                  )}
                </>
              ) : (
                <p className="text-center text-gray-500 text-lg">No tracks found matching your search</p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Playlist Manager */}
      {currentUser && (
        <PlaylistManager
          playlists={playlist.playlists}
          selectedPlaylist={playlist.selectedPlaylist}
          onSelectPlaylist={(p) => {
            playlist.setSelectedPlaylist(p);
            setShowPlaylistViewer(true);
          }}
          onCreatePlaylist={playlist.createPlaylist}
          onDeletePlaylist={playlist.deletePlaylist}
          onAddTrack={playlist.addTrackToPlaylist}
          onRemoveTrack={playlist.removeTrackFromPlaylist}
          onUpdatePlaylist={playlist.updatePlaylist}
          currentTrack={currentTrack}
          isPlaying={isPlaying}
        />
      )}

      {/* Playlist Viewer Modal */}
      {showPlaylistViewer && currentUser && (
        <PlaylistViewer
          playlist={playlist.selectedPlaylist}
          onClose={() => setShowPlaylistViewer(false)}
          onPlayTrack={handlePlayPlaylist}
          onRemoveTrack={playlist.removeTrackFromPlaylist}
          isPlaying={isPlaying}
          playQueue={playQueue}
          onPauseTrack={player.pause}
          currentTrack={currentTrack}
          currentUser={currentUser}
          isPurchased={isPurchasedFallback}
          onDownload={handleDownload}
        />
      )}

      {/* Purchase Modal */}
     <PurchaseModal
        selectedTrack={purchase.selectedTrack}
        isOpen={purchase.showPriceModal}
        onClose={() => purchase.setShowPriceModal(false)}
        onSubmit={purchase.handleCheckout}  // ← FIX THIS LINE
        isProcessing={purchase.processingPayment === purchase.selectedTrack?.id}
        purchaseSuccess={purchase.purchaseSuccess}
        price={purchase.PADDLE_PRICES.PRICE_15.amount}
        priceData={purchase.PADDLE_PRICES.PRICE_15}
        currentUser={currentUser}
        priceError={purchase.priceError}  // ← ADD THIS LINE
      />

      {/* Add to Playlist Modal */}
      <AddToPlaylistModal
        track={trackToAddToPlaylist}
        playlists={playlist.playlists}
        isOpen={showAddToPlaylistModal}
        onClose={() => {
          setShowAddToPlaylistModal(false);
          setTrackToAddToPlaylist(null);
        }}
        onAddToPlaylist={playlist.addTrackToPlaylist}
        currentUser={currentUser}
      />
    </>
  );
}